<template>
  <div id="app-container" v-bind:class="{ wide: isGrid }">
    <div id="app">
      <!-- Login -->
      <section id="login" v-if="!user.success">
        <div id="login_left">
          <Logo color="#FFFFFF" />
          <LoginImg />
        </div>
        <div id="login_right">
          <a
            class="btn uppercase"
            v-if="showLoginButton"
            :href="user.redirect_uri"
            >Login</a
          >
          <!-- Progress Bar -->
          <ul class="prog-bar">
            <li>Login</li>
            <li>Choose Timeframe</li>
            <li>Review Toggl</li>
          </ul>
        </div>
      </section>

      <!-- Timeframe Select -->
      <section id="timeframe" v-if="user.success && !isGrid">
        <header>
          <div class="account">
            <span class="bold uppercase">Logged in as: </span>
            <span>{{ user.email }}</span>
            <a href="#" class="uppercase" @click="logout()">Log Out</a>
          </div>
          <div class="logo">
            <Logo color="#110E17" />
          </div>
          <div class="go-back uppercase">
            <router-link to="/" v-bind:class="{ hide: !isGrid }">
              <BackArrow />
              <span>Back to Timeframe</span>
            </router-link>
          </div>
        </header>
        <div class="section-inner">
          <p>Choose the dates in which you wish to add to Tempo.</p>
          <h1>Choose The Timeframe</h1>
          <div class="timeframe-wrapper">
            <div class="datepickers">
              <div>
                <label>From</label>
                <input v-model="toggl.start" value:="toggl.start"
                @change="setSubmitStatus" type="date" label="Start" />
              </div>
              <div>
                <label>To</label>
                <input v-model="toggl.end" value:="toggl.end"
                @change="setSubmitStatus" type="date" label="End" />
              </div>
            </div>
            <button
              class="btn uppercase"
              @click="exportWorklogs()"
              :disabled="isSubmitDisabled"
            >
              Sync Hours From Toggl
            </button>
            <!-- Progress Bar -->
            <ul class="prog-bar login">
              <li class="checked">Login</li>
              <li>Choose Timeframe</li>
              <li>Review Toggl</li>
            </ul>
            <button
              class="btn uppercase white"
              @click="deleteWorklogs()"
              :disabled="isSubmitDisabled"
            >
              DELETE existing logs
            </button>
          </div>
        </div>
      </section>

      <!-- Grid -->
      <section id="grid" v-if="user.success && isGrid">
        <header>
          <div class="account">
            <span class="bold uppercase">Logged in as: </span>
            <span>{{ user.email }}</span>
            <a href="#" class="uppercase" @click="logout()">Log Out</a>
          </div>
          <div class="logo">
            <Logo color="#110E17" />
          </div>
          <div class="go-back uppercase">
            <router-link to="/" v-bind:class="{ hide: !isGrid }">
              <BackArrow />
              <span>Back to Timeframe</span>
            </router-link>
          </div>
        </header>
        <div class="grid-body">
          <div class="action-bar">
            <p>
              You are looking at time logs from
              <span class="bold">{{ formatGridDate(toggl.start) }}</span> to
              <span class="bold">{{ formatGridDate(toggl.end) }}</span
              >. Please ensure all fields are filled in before adding the logs
              to toggl. Any missing fields will be highlighted in
              <span class="bold yellow">yellow</span>.
            </p>
            <button
              class="btn uppercase"
              @click="importWorklogs()"
              :disabled="toggl.running"
            >
              Add Time to Tempo
            </button>
            <button
              class="btn uppercase"
              @click="openTempo()"
            >
              Open Tempo
            </button>
          </div>
          <div class="grid-sheet">
            <div id="sheet"></div>
            <!-- <p align="center"> 
              <table>
                <tr>
                  <th>Project Key</th>
                  <th>Account Name</th>
                  <th>Account Key</th>
                </tr>
                <tbody v-for="project in accountLookup" v-bind:key="project.projectKey">
                  <tr v-for="account in project.accounts.accounts" v-bind:key="account.id">
                    <td>{{ project.projectKey }}</td>
                    <td>{{ account.name }}</td>
                    <td>{{ account._fulldata.key }}</td>
                  </tr>
                </tbody>
              </table>
            </p> -->
          </div>
          <div>
            <!-- <button @click="reloadDynamicData()">Reload Dynamic Data</button>
            <button @click="reset()">Reset</button> -->
          </div>
        </div>
        <!-- Progress Bar -->
        <ul class="prog-bar timeframe">
          <li class="checked">Login</li>
          <li class="checked">Choose Timeframe</li>
          <li :class="showProgress ? 'checked' : ''">Review Toggl</li>
        </ul>
      </section>

      <!-- Modals -->
      <section id="modals" v-bind:class="{ active: showModal }">
        <!-- Error Modal -->
        <div class="modal error" v-bind:class="{ active: showError }">
          <button @click="clearModals()">&times;</button>
          <div><span class="bold">Error Code:</span> xxxxxxxxxx</div>
          <p v-html="errorMsg"></p>
        </div>

        <!-- Delete progress modal --> 
        <div class="modal progress-delete" v-bind:class="{ active: showDeleteProgress }">
          <div class="progress-wrapper">
            <div
              class="progress"
              v-bind:style="{ width: deleteComplete + '%' }"
            ></div>
          </div>
          <div id="in-progress" v-bind:class="{ hide: deleteComplete }">
            <h2>Almost Done!</h2>
            <p>Please note, due to features that are being developed, the progress bar will not update incrementally. The progress bar will remain at 0% until all of the logs have been deleted, then move to 100% instantly.</p>
          </div>
          <div id="complete" v-bind:class="{ hide: !deleteComplete }">
            <h2>Complete!</h2>
          </div>
          <DeleteModalImg />
        </div>
        <!-- Progress Modal -->
        <div class="modal progress" v-bind:class="{ active: showProgress }">
          <div class="progress-wrapper">
            <div
              class="progress"
              v-bind:style="{ width: percentComplete + '%' }"
            ></div>
          </div>
          <div id="in-progress" v-bind:class="{ hide: uploadComplete }">
            <h2>Almost Done!</h2>
            <p>Please note, due to features that are being developed, the progress bar will not update incrementally. The progress bar will remain at 0% until all of the logs are complete, then move to 100% instantly.</p>
          </div>
          <div id="complete" v-bind:class="{ hide: !uploadComplete }">
            <h2>Complete!</h2>
            <button class="btn uppercase" @click="openTempo()">
              Open Tempo
            </button>
          </div>
          <ModalImg />
        </div>

        <!-- Delete Modal -->
        <div class="modal delete" v-bind:class="{ active: showDelete }">
          <button class="close" @click="clearModals()">&times;</button>
          <h2>Success!</h2>
          <p>Your chosen logs have been successfully deleted.</p>
          <div>
            <button class="btn uppercase" @click="openTempo()">
              Open Tempo
            </button>
          </div>
          <ModalImgTrash />
        </div>
      </section>
    </div>
  </div>
</template>

<script>
// import csv from "csvtojson";
import axios from "axios";
import Spreadsheet from "x-data-spreadsheet";

import Logo from "./components/Logo";
import LoginImg from "./components/LoginImg";
import ModalImg from "./components/ModalImg";
import DeleteModalImg from "./components/DeleteModalImg";
import ModalImgTrash from "./components/ModalImgTrash";
import BackArrow from "./components/BackArrow";

axios.defaults.withCredentials = true;

export default {
  name: "App",
  components: { Logo, LoginImg, ModalImg, ModalImgTrash, BackArrow, DeleteModalImg },
  metaInfo: {
    title: "Trellis - Obelisk",
  },
  created: function () {
    // Tempo
    if (this.$route.query.code) {
      this.token = this.$route.query.code;
      this.$cookies.set("tempo_token", this.$route.query.code);
    } else {
      this.token = this.$cookies.get("tempo_token") || null;
    }
    if (this.$route.query.jira_code) {
      this.jira_token = this.$route.query.jira_code;
      this.$cookies.set("jira_token", this.$route.query.jira_code);
    } else {
      this.jira_token = this.$cookies.get("jira_token") || null;
    }
    this.jiraAccountId = this.$cookies.get("jira_account_id") || null;

    // Toggl
    let _auth = this.$cookies.get("toggl_auth");
    if (_auth && _auth != "") {
      this.toggl.auth = _auth;
      this.toggl.tested = true;
    }
  },
  data: function () {
    function formatDate(date) {
      var d = new Date(date),
        month = "" + (d.getMonth() + 1),
        day = "" + d.getDate(),
        year = d.getFullYear();

      if (month.length < 2) month = "0" + month;
      if (day.length < 2) day = "0" + day;

      return [year, month, day].join("-");
    }

    let today = new Date();
    let oneWeekAgo = new Date();
    oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);

    return {
      spreadsheet: null,
      lastState: null,
      user: {
        success: false,
        redirect_uri:
          this.baseUrl + "/login?redirect=" + "http://localhost:8081",
      },
      accountLookup: null,
      jiraAccountId: null,
      token: null,
      jira_token: null,
      worklogFile: null,
      baseUrl: process.env.VUE_APP_API_BASE_URL,
      tempoBase: "/tempo/oauth/start",
      togglBase: "/toggl/worklogs",
      tempoAccountsBase: "/tempo/accounts?projects=",
      isSubmitDisabled: false,
      isGrid: false,
      showModal: false,
      showError: false,
      showProgress: false,
      showDeleteProgress: false, 
      // deleteProgress: false,
      deleteComplete: false,
      showDelete: false,
      uploadComplete: false,
      percentComplete: 0,
      errorMsg: "",
      toggl: {
        running: false,
        auth: "",
        email: "",
        password: "",
        start: formatDate(oneWeekAgo),
        end: formatDate(today),
        tested: false,
      }
    };
  },
  mounted: function () {
    axios
      .get(this.baseUrl + "/user")
      .then((result) => (this.user = result.data));

    // Reset Route on Refresh
    if (this.$router.history.current.path === "/grid") {
      this.$router.push("/");
    }

    // this.initSheet();
  },
  computed: {
    loginUrl: function () {
      return this.baseUrl + this.tempoBase;
    },
    showLoginButton: function () {
      return !this.user.success && this.user.redirect_uri;
    },
  },
  watch: {
    $route(to) {
      this.isGrid = to.path === "/grid" ? true : false;
    }
  },
  methods: {
    initSheet: function () {
      // Bind events:
      var _component = this;

      // Returns a function, that, as long as it continues to be invoked, will not
      // be triggered. The function will be called after it stops being called for
      // N milliseconds. If `immediate` is passed, trigger the function on the
      // leading edge, instead of the trailing.
      function debounce(func, wait, immediate) {
        var timeout;
        return function () {
          var context = this,
            args = arguments;
          var later = function () {
            timeout = null;
            if (!immediate) func.apply(context, args);
          };
          var callNow = immediate && !timeout;
          clearTimeout(timeout);
          timeout = setTimeout(later, wait);
          if (callNow) func.apply(context, args);
        };
      }

      const ROUNDING_SECONDS_FACTOR = 300

      function roundSeconds(input) {
          var d = Number(input);
          if(d > 0)
              d = Math.ceil(d / ROUNDING_SECONDS_FACTOR) * ROUNDING_SECONDS_FACTOR;
          else if(d < 0)
              d = Math.floor(d / ROUNDING_SECONDS_FACTOR) * ROUNDING_SECONDS_FACTOR;
          else
              d = ROUNDING_SECONDS_FACTOR;
          return d
      }

      function fromSeconds(input) {
          var d = roundSeconds(input)
          var h = Math.floor(d / 3600);
          var m = Math.floor(d % 3600 / 60);
          var s = Math.floor(d % 3600 % 60);
          return [
              h > 0 ? (h + "h") : null,
              m > 0 ? (m + "m") : null,
              s > 0 ? (s + "s") : null
          ].join(" ").trim();
      }

      function toSeconds(input) {
          /* eslint-disable-next-line */
          var timeSpent = /(([0-9]*)(h))?\ ?(([0-9]*)(m))?\ ?(([0-9]*)(s))?/g.exec(input);
          var seconds = (parseInt(timeSpent[2] || 0) * 60 * 60) + parseInt((timeSpent[5] || 0) * 60) + parseInt(timeSpent[8] || 0);
          return roundSeconds(seconds)
      }

      function initializeSpreadsheet(existingState) {

        let validate = function () {
          console.log("validate triggered." + Date.now()); 
            // For each row, validate:
            for (
              let row = 1;
              row < _component.spreadsheet.datas[0].rows.len;
              row++
            ) {
              // For each column, validate:
              for (
                let column = 0;
                column < _component.spreadsheet.datas[0].cols.len;
                column++
              ) {
                // this.lastState[row][column] =
                // Update style:
                let cell = _component.spreadsheet.cell(row, column);

                // If no changes, abort early:
                if (cell.text == _component.lastState[row][column]) {
                  continue;
                }

                // We have encountered a new value, store in memory for next time:
                _component.lastState[row][column] = cell.text;

                // Grab value from cell:
                let value = cell.text ?? ""
                let header = _component.spreadsheet.cell(0, column).text
                let status = _component.spreadsheet.cell(row, 7).text.filter(item => item.indexOf(header) === -1)

                // If cell value is not empty, remove empty cell style:
                if (value && value != "" && value.length > 0) {
                  cell.style = "default";
                  // If we  are in the JIRA ticket cell, re-do it's Account dropdown:
                  if (column == 0) {
                    var jira_matcher = /((?!([A-Z0-9a-z]{1,10})-?$)[A-Z]{1}[A-Z0-9]+-\d+)/g;
                    if (
                      value != null &&
                      value != "" &&
                      typeof value === "string"
                    ) {
                      let matches = value.match(jira_matcher);
                      if (matches && matches.length > 0) {
                        status.length === 0 ? _component.spreadsheet.cellText(row, 7, ["N/A"]).reRender() : _component.spreadsheet.cellText(row, 7, status.filter(item => item.indexOf(header) === -1)).reRender()
                        _component.reloadAccountDropdown(row);
                      } else {
                        status = status.filter(item => item !== 'N/A')
                        _component.spreadsheet.cell(row, column).style = "errorCell"
                        status.push(header + ": Bad format")
                        _component.spreadsheet.cellText(row, 7, [...new Set(status)]).reRender()
                      }
                    }
                  } else if (column == 1) {
                    // If we are in the timeSpend cell
                    var time_matcher = /^((?:\s*\d{1,3}[h]\s+|\s*\d{1,3}[h]\s*$)?(?:\s*\d{1,3}[m]\s*|\s*\d{1,3}[m]\s*$)?(?:\s*\d{1,3}[s]\s*|\s*\d{1,3}[s]\s*$)?)$/g
                    if (
                      value != null &&
                      value != "" &&
                      typeof value === "string"
                    ) {
                      let matches = value.match(time_matcher)
                      if (matches && matches.length > 0) {
                        status.length === 0 ? _component.spreadsheet.cellText(row, 7, ["N/A"]).reRender() : _component.spreadsheet.cellText(row, 7, status.filter(item => item.indexOf(header) === -1)).reRender()
                      } else {
                        status = status.filter(item => item !== 'N/A')
                        _component.spreadsheet.cell(row, column).style = "errorCell"
                        status.push(header + ": Bad format")
                        _component.spreadsheet.cellText(row, 7, [...new Set(status)]).reRender()
                      }
                    }
                    // START: NOV-600 - Rounding logic: 
                    var raw_seconds = toSeconds(value)
                    var rounded_value = fromSeconds(raw_seconds)
                    if (value != rounded_value) {
                      _component.spreadsheet.cellText(row, column, rounded_value).reRender()
                    }
                    // END INOV-600 
                    
                  } else if (column == 3) {
                    // If we are in the startDate cell
                    var date_matcher = /^\d{4}-(0[1-9]|1[012])-(0[1-9]|[12][0-9]|3[01])$/g
                    if (
                      value != null &&
                      value != "" &&
                      typeof value === "string"
                    ) {
                      let matches = value.match(date_matcher)
                      if (matches && matches.length > 0) {
                        status.length === 0 ? _component.spreadsheet.cellText(row, 7, ["N/A"]).reRender() : _component.spreadsheet.cellText(row, 7, status.filter(item => item.indexOf(header) === -1)).reRender()
                      } else {
                        status = status.filter(item => item !== 'N/A')
                        _component.spreadsheet.cell(row, column).style = "errorCell"
                        status.push(header + ": Bad format")
                        _component.spreadsheet.cellText(row, 7, [...new Set(status)]).reRender()
                      }
                    }
                  } else if (column == 5 && _component.accountLookup) {
                    // If we are in the Account cell
                    if (value != "") {

                      // 1. Get list of valid accounts for issue key: 
                      let foundAccount = false;
                      let projectKey = _component.spreadsheet.cell(row, 0).text.split("-")[0];
                      let accounts =  _component.accountLookup.find(x => x.projectKey == projectKey);
                      if (accounts) {
                        let account = accounts.accounts.accounts.find(x => x.name == value);
                        if (account) {
                          foundAccount = true; 
                          cell.style = "default";
                        }
                      }                      
                      if (!foundAccount) {
                        status = status.filter(item => item !== 'N/A')
                        status.push(header + ": Unknown"); 
                        _component.spreadsheet.cell(row, column).style = "errorCell"
                        _component.spreadsheet.cellText(row, 7, [...new Set(status)]).reRender()
                      } else {
                        status.length === 0 ? _component.spreadsheet.cellText(row, 7, ["N/A"]).reRender() : _component.spreadsheet.cellText(row, 7, status.filter(item => item.indexOf(header) === -1)).reRender()
                      }
                    }
                  } else if (column == 6) {
                    // If we are in the action cell
                    var action_matcher = /(\W|^)INSERT|IGNORE(\W|$)/g
                    if (
                      value != null &&
                      value != "" &&
                      typeof value === "string"
                    ) {
                      let matches = value.match(action_matcher)
                      if (matches && matches.length > 0) {
                        status.length === 0 ? _component.spreadsheet.cellText(row, 7, ["N/A"]).reRender() : _component.spreadsheet.cellText(row, 7, status.filter(item => item.indexOf(header) === -1)).reRender()
                      } else {
                        status = status.filter(item => item !== 'N/A')
                        _component.spreadsheet.cell(row, column).style = "errorCell"
                        status.push(header + ": Unknown action")
                        _component.spreadsheet.cellText(row, 7, [...new Set(status)]).reRender()
                      }
                    }
                  } else {
                    _component.spreadsheet.cellText(row, 7, status.length > 0 ? status : ['N/A']).reRender()
                  }
                } else {
                  // Otherwise, we have encountered a blank cell, make it have the blank cell style:
                  // Column 5 allows a blank one, so skip it: 
                  if (column != 5) {
                     _component.spreadsheet.cellText(row, column, "");
                    _component.spreadsheet.cell(row, column).style = "blankCell";
                    let headers = []

                    if(value === '' && column !== 5) {
                      headers.push(_component.spreadsheet.cell(0, column).text)
                      status = status.filter(item => item !== 'N/A')
                      status.push(...headers.map(header => header + ": Missing"))
                      _component.spreadsheet.cellText(row, 7, [...new Set(status)]).reRender()
                    }
                  }
                  
                }
              }
            }
        }

        // Nuke any existing one:
        document.getElementById("sheet").innerHTML = "";

        // Re-instantiate:
        _component.spreadsheet = new Spreadsheet("#sheet", {
          showToolbar: false,
          showGrid: true,
          showContextmenu: true,
          view: {
            height: () =>
              Math.max(
                document.documentElement.clientHeight - 200 || 0,
                window.innerHeight - 200 || 0
              ) * 0.8,
          },
        });

        // Add hack for reacting to element clicks: 
        document.getElementById('sheet').addEventListener('click', function () {
          validate();
        })
        document.getElementById('sheet').addEventListener('keyup', function () {
          validate(); 
        })

        // If the state was passed in (likely from a resize func), load it now:
        if (existingState) {
          _component.spreadsheet.loadData(existingState);
        }

        // Add a developer shortcut to access the Spreadsheet OM:
        window._s = _component.spreadsheet;

        // Add our spreadsheet change event to handle the dynamic updates:
        _component.spreadsheet.on(
          "change",
          debounce(validate, 50)
        );
      }

      // Re-initialize on resize of window (To make spreadsheet UI proportional / re-evaluate the width + height funcs):
      window.onresize = debounce(function () {
        let state = _component.spreadsheet.getData();
        initializeSpreadsheet(state);
      }, 250);

      // First run initialize the sheet:
      initializeSpreadsheet(null);
    },
    setSubmitStatus: function () {
      if (this.toggl.start != "" && this.toggl.end != "") {
        this.isSubmitDisabled = false;
      }
    },
    formatGridDate(d) {
      let date = new Date(d);
      let year = date.getFullYear();

      let month = (1 + date.getMonth()).toString();
      month = month.length > 1 ? month : "0" + month;

      let day = (1 + date.getDate()).toString();
      day = day.length > 1 ? day : "0" + day;

      return month + "/" + day + "/" + year;
    },
    clearModals() {
      this.showModal = false;
      this.showError = false;
      this.showProgress = false;
      this.uploadComplete = false;
    },
    openTempo() {
      window.open(
        "https://growwithtrellis.atlassian.net/plugins/servlet/ac/io.tempo.jira/tempo-app#!/my-work/week?type=LIST",
        "_blank"
      );
      this.clearModals();
      // OBEL-53 - Don't route back to root: this.$router.push("/");
    },
    // Tempo Methods
    clearJiraAuthorization() {
      this.jira_token = null;
      this.jiraAccountId = null;
      this.$cookies.remove("jira_token");
      this.$cookies.remove("jira_account_id");
      this.$router.push("/tempo");
    },
    manualUpdateJiraAccountId() {
      this.$cookies.set("jira_account_id", this.jiraAccountId);
      this.$router.push("/tempo");
    },
    reset() {
      /* const defaultData = [
        {
          name: "Toggl Dump",
          rows: { len: 10 },
          cols: {
            len: 8,
            2: { width: 439 },
            6: { width: 25 },
          },
          validations: [],
        },
      ];
      this.spreadsheet.loadData(defaultData);
      this.accountLookup = null; */
    },
    updateJiraAccountId() {
      axios
        .get(
          "https://api.atlassian.com/ex/jira/2fc665dd-282d-4c11-94e7-412a6929f63b/rest/api/3/myself",
          {
            headers: {
              authorization: "Bearer " + this.jira_token,
            },
          }
        )
        .then((response) => {
          this.jiraAccountId = response.data.accountId;
          this.$cookies.set("jira_account_id", this.jiraAccountId);
        })
        .catch((e) => {
          window.alert("ERROR: " + e.message);
        });
    },
    logoutTempo() {
      this.token = null;
      this.$router.push("/tempo");
      this.$cookies.remove("tempo_token");
    },
    loadTextFromFile(ev) {
      this.worklogFile = ev.target.files[0];
    },
    logout() {
      axios.get(this.baseUrl + "/logout").then(function (response) {
        window.location = response.data.logout_uri;
      });
    },
    updateRow(rowIndex, action, status) {
      console.log("UPDATE ROW: ", rowIndex, action, status)
      this.spreadsheet
        .cellText(rowIndex, 6, action)
        .cellText(rowIndex, 7, status)
        .reRender();
    },
    importWorklogs() {
      this.showDeleteProgress = false;
      this.showDelete = false;
      this.showModal = true;
      this.showProgress = true;
      var records = [];
      var sheet = this.spreadsheet.getData()[0];
      var headers = null;
      for (var i = 0; i < sheet.rows.len; i++) {
        let row = sheet.rows[i];
        if (i == 0) {
          headers = row.cells.map((cell) => cell.text);
          continue;
        }
        var entry = headers.reduce(function (accumulator, header, index) {
          var value = "";
          if (row.cells[index]) {
            let cellText = row.cells[index].text;
            let text = Array.isArray(cellText) ? cellText[0] : cellText;
            value = text ?? "";
          }
          accumulator[header] = value;
          return accumulator;
        }, {});
        entry.rowIndex = i;
        records.push(entry);
      }

      // Only process "INSERT" records: 
      records = records.filter(record => record.action == 'INSERT'); 


      var successfulImports = 0;
      var component = this;
      component.toggl.running = true;
      axios.post(component.baseUrl + "/tempo/worklog", { records: records }, { responseType: "application/json", }).then(function (response) {
        console.log(response);  
        for (let row of response.data) {
          console.log(row)
          if (row.errors || !row.success) {
            console.log('pig ', row.errors, !row.success)
            let _msg = '';
            if (
              row.errors &&
              row.errors.length > 0 &&
              row.errors[0].message
            ) {
                _msg = "Tempo: " + row.errors[0].message;
                if (_msg == 'MISSING_TOGGL_TOKEN') {
                  _msg = `Your Toggl API key is missing, please <a href='https://app.tettra.co/teams/trellis/pages/how-to-use-obelisk-20-to-log-hours-in-tempo'>add your Toggl API key to HUB</a>`
                }
            }
            if (row.reason) {
              _msg = row.reason;
            }
            var message = "❌ " + _msg;
            console.log(message, row);
            component.updateRow(
              row['_record'].rowIndex,
              "INSERT",
              message
            );
            component.errorMsg = _msg;
            component.showProgress = false;
            component.showError = true;
            component.showModal = true;
            component.toggl.running = false;
          } else {
            component.updateRow(
                row['_record'].rowIndex,
                "IGNORE",
                response.status == 200
                  ? "Imported ✅"
                  : JSON.stringify(row)
            );
          }
          
          successfulImports += 1;
          component.updateImportProgress(records.length, successfulImports);
          if (records.length == successfulImports) {
            component.uploadComplete = true;
            component.toggl.running = false;
          }
        }
      }).catch(function (error) {
        component.errorMsg = error.message;
        component.showProgress = false;
        component.showError = true;
        component.toggl.running = false;
      });

      // Handle case where there's no records to process: 
      if (records.length < 1) {
        this.showModal = false;
        this.showProgress = false;
        this.toggl.running = false; 
        this.uploadComplete = true;
      }
    },
    updateImportProgress: function (total, complete) {
      this.percentComplete = (complete / total) * 100;
    },

    // Toggl Methods
    getAuth: function () {
      return (
        "Basic " +
        new Buffer(this.toggl.email + ":" + this.toggl.password).toString(
          "base64"
        )
      );
    },
    login() {
      this.toggl.auth = this.getAuth();
      this.togglCredentialsWork((success) => {
        if (success) {
          this.toggl.tested = true;
          this.$cookies.set("toggl_auth", this.getAuth());
          return;
        }
        window.alert("Toggl Login failed!");
        this.logoutToggl();
      });
    },
    togglCredentialsWork(callback) {
      var url = `${this.baseUrl}${this.togglBase}?start=2020-02-01&end=2020-02-02`;
      axios
        .get(url, {
          headers: {
            Authorization: this.toggl.auth,
          },
        })
        .then(() => {
          callback(true);
        })
        .catch((error) => {
          console.log(error);
          callback(false);
        });
    },
    logoutToggl() {
      this.toggl.auth = null;
      this.toggl.tested = false;
      this.$router.push("/tempo");
      this.$cookies.remove("toggl_auth");
    },

    async reloadAccountDropdown(rowIndex) {
      // Pull existing validations:
      var validations = this.spreadsheet.datas[0].validations.getData();
      var issueKey = this.spreadsheet.cell(rowIndex, 0).text;
      var projectName = issueKey.split("-")[0].trim();
      var matchingProjects = this.accountLookup.filter(
        (x) => x.projectKey == projectName
      );
      let status = this.spreadsheet.cell(rowIndex, 7).text.filter(item => item.indexOf('issueKey') === -1).filter(item => item !== 'N/A')
      if (matchingProjects.length < 1) {
        this.spreadsheet.cell(rowIndex, 0).style = "errorCell"
        status.push("issueKey: Non-existent JIRA ticket")
        this.spreadsheet.cellText(rowIndex, 7, [...new Set(status)]).reRender()
        return;
      }
      var newValue = matchingProjects[0].accounts.accountsSerialized;

      // Check to see if one already exists, and if so just update it:
      let foundEntry = false;
      for (let validationIndex of validations.keys()) {
        if (validations[validationIndex].refs[0] == "F" + (rowIndex + 1)) {
          validations[validationIndex].value = newValue;
          foundEntry = true;
        }
      }

      // Otherwise, add a new one:
      if (!foundEntry) {
        validations.push({
          mode: "cell",
          operator: "be",
          refs: ["F" + (rowIndex + 1)],
          required: true,
          type: "list",
          value: newValue,
        });
      }
 
      // console.log(validations);

      // Reload the validations:
      this.spreadsheet.datas[0].validations.setData(validations);

      // Set the cell to blank:
      this.spreadsheet.cellText(rowIndex, 5, "").reRender();
    },

    async reloadDynamicData() {
      // Generate new validation rules:
      var rows = this.spreadsheet.getData()[0].rows;
      var rowCount = rows.len;
      var issueKeys = [];
      var cellText = "";
      var i = 1;
      for (i = 1; i < rowCount; i++) {
        cellText = rows[i].cells[0].text;
        let text = Array.isArray(cellText) ? cellText[0] : cellText;
        issueKeys.push(text ?? "");
      }
      var uniqueProjects = issueKeys.reduce(function (accumulator, issueKey) {
        var projectName = issueKey.split("-")[0].trim();
        if (projectName != "" && !accumulator.includes(projectName)) {
          accumulator.push(projectName);
        }
        return accumulator;
      }, []);
      try {
        const accountLookup = await axios.get(
          `${this.baseUrl}${this.tempoAccountsBase}${uniqueProjects.join(",")}`
        );
        this.accountLookup = Object.keys(accountLookup.data).map(function (
          key
        ) {
          return {
            projectKey: key,
            accounts: accountLookup.data[key],
          };
        });
        var validationValues = issueKeys.map(function (issueKey) {
          let projectName = issueKey.split("-")[0].trim();
          if (projectName != "") {
            if (accountLookup.data[projectName]) {
              return accountLookup.data[projectName].accountsSerialized;
            }
          }
          return "";
        });

        // INSERT NEW VALIDATION RULES:
        var spreadsheetData = JSON.parse(
          JSON.stringify(this.spreadsheet.getData())
        );
        spreadsheetData[0].validations = spreadsheetData[0].validations.reduce(
          function (accumulator, validation) {
            if (validation.refs.filter((x) => x.startsWith("F")).length == 0) {
              accumulator.push(validation);
            }
            return accumulator;
          },
          []
        );
        spreadsheetData[0].validations.push(
          ...validationValues.map(function (validation, index) {
            let cellAddress = "F" + (index + 2);
            return {
              refs: [cellAddress],
              mode: "cell",
              type: "list",
              required: false,
              operator: "be",
              value: validation,
            };
          })
        );
        this.spreadsheet.loadData(spreadsheetData);

        // Generate default last state =
        this.lastState = {};
        for (let row = 1; row < this.spreadsheet.datas[0].rows.len; row++) {
          // For each column, validate:
          for (
            let column = 0;
            column < this.spreadsheet.datas[0].cols.len;
            column++
          ) {
            if (!this.lastState[row]) {
              this.lastState[row] = {};
            }
            this.lastState[row][column] = this.spreadsheet.cell(
              row,
              column
            ).text;
          }
        }
      } catch (e) {
        console.log(e.message)
      }
    },
    deleteWorklogs() {
      this.showProgress = false;
      this.showModal = true;
      this.showDeleteProgress = true;
      this.deleteComplete = 0;
      
      let start = this.toggl.start,
          end = this.toggl.end;
      var url = `${this.baseUrl}${this.togglBase}?start=${start}&end=${end}`;
      axios
        .delete(url, {
          headers: {
            Authorization: this.toggl.auth,
          },
        })
        .catch((error) => {
          console.log(error.message);
        })
        .then(() => {
          console.log('deleteWorklogs')
          this.deleteComplete = 5;
          this.showDeleteProgress = false
          this.showDelete = true
        })
    }, 
    exportWorklogs() {
      this.$router.push("/grid");
      setTimeout(() => {
        this.initSheet();

        let start = this.toggl.start,
          end = this.toggl.end;
        let timezoneOffset = new Date().getTimezoneOffset();
        let component = this; 
        var url = `${this.baseUrl}${this.togglBase}?start=${start}&end=${end}&timezoneOffset=${timezoneOffset}`;

        axios
          .get(url, {
            headers: {
              Authorization: this.toggl.auth,
            },
          })
          .then((response) => {
            var togglData = response.data.map(function (row) {
              var cells = Object.keys(row).map(function (key) {
                var cell = {};
                if (
                  !Object.prototype.hasOwnProperty.call(row, key) ||
                  row[key].toString().trim() == ""
                ) {
                  cell.style = "blankCell";
                  cell.text = "";
                } else {
                  cell.text = row[key];
                }
                return cell;
              });
              cells.push(
                ...[{ text: "" }, { text: "INSERT" }, { text: ["N/A"] }]
              );
              return {
                height: 60,
                cells: cells,
              };
            });
            var header = Object.keys(response.data[0]).map((header) => {
              return { text: header, style: "boldedHeader", editable: false };
            });
            header.push({
              text: "accountName",
              style: "boldedHeader",
              editable: false,
            });
            header.push({
              text: "action",
              style: "boldedHeader",
              editable: false,
            });
            header.push({
              text: "status",
              style: "boldedHeader",
              editable: false,
            });
            togglData.unshift({
              cells: header,
            });
            var _rows = { ...togglData };
            _rows.len = togglData.length;
            const myData = [
              {
                name: "Toggl Dump",
                rows: _rows,
                cols: {
                  len: 8,
                  2: { width: 350 },
                  5: { width: 105 },
                  6: { width: 65 },
                  7: { width: 300 }
                },
                validations: [
                  {
                    mode: "cell",
                    operator: "be",
                    refs: ["G2:G9999"],
                    required: true,
                    type: "list",
                    value: "INSERT,IGNORE", // ,DELETE,REMOVEDUPE",
                  }
                ],
                styles: {
                  errorCell: {
                    bgcolor: "#c00000",
                    color: "#ffffff",
                    font: {
                      bold: true,
                    }
                  },
                  blankCell: {
                    bgcolor: "#f1c232",
                  },
                  default: {},
                  boldedHeader: {
                    font: {
                      bold: true,
                    }
                  }
                }
              }
            ];

            // Procedurally generate validations (drop downs):
            // TARGET FORMAT: "validations":[{"refs":["A1"],"mode":"cell","type":"list","required":false,"operator":"be","value":"Nick,Jeff,Steve"}]

            // Handle cell conversion:

            // End procedural drop downs
            this.spreadsheet.loadData(myData);
            this.reloadDynamicData();
            // const blob = new Blob([response.data], { type: "application/csv" });
            // const link = document.createElement("a");
            // link.href = URL.createObjectURL(blob);
            // link.download = "worklogs.csv";
            // link.click();
            // URL.revokeObjectURL(link.href);
            Object.values(this.spreadsheet.datas[0].rows["_"])
            .map((row, ri) => {
              let headers = []
              return row.cells
              .filter((cell, ci) => {
                if(cell.text === '' && ci !== 5) {
                  headers.push(this.spreadsheet.cell(0, ci).text)
                  this.spreadsheet.cellText(ri, 7, headers.map((header, index) => (index > 0 ? ' ' : '') + header + ": Missing")).reRender()
                }
              })
            })
          })
          .catch((error) => {
            var errorMessage = error.message;
            if (error.response && error.response.data) {
              errorMessage = error.response.data;
            }
            
            var _msg = errorMessage;
            if (typeof errorMessage === "string") {
              errorMessage = errorMessage.replace(
                /(Request failed with status code )([0-9]* : )/,
                ""
              );
              errorMessage = JSON.parse(errorMessage);
            }
            
            if (
                errorMessage.errors &&
                errorMessage.errors.length > 0 &&
                errorMessage.errors[0].message
            ) {
                _msg = errorMessage.errors[0].message;
                if (_msg == 'MISSING_TOGGL_TOKEN') {
                  _msg = `Your Toggl API key is missing, please <a target="_blank" href='https://app.tettra.co/teams/trellis/pages/how-to-use-obelisk-20-to-log-hours-in-tempo#header-cubn1-add-your-toggl-api-key-to-hub'>add your Toggl API key to HUB</a>`
                }
            }

            if (errorMessage.reason) {
              _msg = errorMessage.reason;
            }
            console.log("MSG: " + _msg);
            component.errorMsg = _msg;
            component.showProgress = false;
            component.showError = true;
            component.showModal = true;
            component.toggl.running = false;
            throw error;
          });
      }, 10);
    }
  }
};
</script>

<style>
@import url("https://fonts.googleapis.com/css2?family=Barlow:wght@600&display=swap");
@import url("https://fonts.googleapis.com/css2?family=Roboto:wght@400;700&display=swap");

* {
  box-sizing: border-box;
}
html,
body,
body > div {
  height: 100%;
}
body {
  margin: 0;
  font-family: "Roboto", sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: #110e17;
}
#app-container {
  display: flex;
  align-items: center;
  height: 100vh;
  background: #f6f6f6;
}
#app-container.wide {
  height: 100%;
}
#app {
  margin: 0 auto;
  width: 1000px;
  height: 600px;
  background: #fff;
  box-shadow: 0px 10px 70px rgba(0, 0, 0, 0.35);
  position: relative;
}
.wide #app {
  width: 100%;
  height: 100%;
}
.bold {
  font-weight: 700;
}
.red {
  color: #c00000;
}
.yellow {
  color:#f1c232;
}
.uppercase {
  text-transform: uppercase;
}
.hide {
  display: none !important;
}
a {
  font-family: "Barlow", sans-serif;
  font-size: 14px;
  letter-spacing: 2px;
  color: #88868b;
  text-decoration: none;
}
.btn {
  padding: 14px 15px;
  background: #110e17;
  border: 0;
  border-radius: 2px;
  font-family: "Barlow", sans-serif;
  font-size: 14px;
  letter-spacing: 2px;
  color: #fff;
  cursor: pointer;
}
.white {
  background:transparent;
  color:#110e17;
  border:solid 1px #110e17;
}
button:disabled {
  opacity: 0.5;
  cursor: default;
}
button.close {
  background:transparent;
  border:none;
  padding:0.25rem 0.5rem;
  position:absolute;
  top:0;
  right:0;
  font-size:24px;
}

/* Login Page */
#login {
  display: flex;
  height: 100%;
}
#login_left {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 50%;
  background: #120f18;
}
#login_left .login-logo {
  margin: 40px 0 90px;
}
#login_right {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 50%;
  position: relative;
}

/* Header */
header {
  display: flex;
  padding: 20px;
}
header > div {
  width: 33%;
}
header .account {
  font-size: 12px;
}
header .account a {
  margin-left: 10px;
}
header .logo {
  text-align: center;
}
header .go-back {
  text-align: right;
}
header .go-back a {
  display: flex;
  justify-content: flex-end;
  align-items: center;
}
header .go-back svg {
  margin-right: 10px;
}

/* Timeframe Select Page */
.section-inner {
  padding: 20px;
  text-align: center;
}
.section-inner p,
.grid-body p {
  font-size: 14px;
}
.section-inner h1 {
  margin: 35px 0 30px;
  font-family: "Barlow", sans-serif;
  font-size: 39px;
}
.timeframe-wrapper {
  margin: 0 auto;
  width: 350px;
}
.timeframe-wrapper button {
  margin-top: 34px;
  width: 100%;
}
.datepickers {
  display: flex;
  justify-content: space-between;
}
.datepickers div {
  width: 48%;
}
.datepickers input {
  width: 100%;
  padding: 14px 15px;
  border: 1px solid #dbdce0;
  border-radius: 2px;
  font-family: "Roboto", sans-serif;
  font-size: 14px;
}
.datepickers label {
  display: block;
  margin-bottom: 3px;
  font-size: 12px;
  text-align: left;
}

/* Grid Page */
.grid-body {
  margin: 0 auto;
  width: 1420px;
}
.action-bar {
  display: flex;
  justify-content: space-between;
  margin: 20px 0;
}
.grid-sheet .nav {
  position: absolute;
  width: 100%;
  top: 0;
  left: 0;
}
.grid-sheet .content {
  position: relative;
}
.grid-sheet section {
  width: 100%;
}
.grid-sheet .grid {
  display: grid;
  grid-template-rows: 1fr 1fr;
  height: 100vh;
  overflow-y: auto;
  overflow-x: hidden;
}
.grid-sheet .x-spreadsheet-bottombar {
  display: none;
}
.grid-sheet table,
.grid-sheet th,
.grid-sheet td {
  width: 1024px;
  height: 10px;
  border: 1px solid black;
  border-collapse: collapse;
}

/* Modals */
#modals {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: none;
  justify-content: center;
  align-items: center;
  background: rgba(0, 0, 0, 0.1);
  z-index: 100;
}
#modals.active {
  display: flex;
}
.modal {
  display: none;
  position: relative;
  width: 420px;
  padding: 8px;
  background: #fff;
  border-radius: 3px;
  text-align: center;
}
.modal.active {
  display: block;
}
.modal h2 {
  margin: 17px 0 11px;
  font-family: "Barlow", sans-serif;
  font-size: 34px;
}
.modal.error {
  padding: 44px 48px;
}
.modal p {
  font-size: 14px;
  line-height: 22px;
}
.modal.error button {
  position: absolute;
  top: 8px;
  right: 8px;
  background: transparent;
  border: 0;
  font-size: 24px;
  color: #c4c4c4;
  cursor: pointer;
}
.modal .btn {
  margin-bottom: 22px;
}
.modal.progress p {
  margin-bottom: 50px;
}
.modal .progress-wrapper {
  height: 12px;
  background: #f7f6f8;
}
.modal .progress-wrapper .progress {
  height: 12px;
  background: #50cb75;
  transition: width 0.33s;
}
.x-spreadsheet-suggest {
  width: 437px !important;
}
.prog-bar {
  list-style: none;
  justify-content: center;
  display: flex;
  font-size: 8px;
  font-family: "Barlow", sans-serif;
  letter-spacing: 2px;
  text-transform: uppercase;
  width: 280px;
  margin: 0 auto;
  text-align: center;
  padding: 0;
  position: absolute;
  bottom: 30px;
  left: calc(50% - 140px);
}
.prog-bar.login {
  position:relative;
  bottom:unset;
  left:unset;
  margin-top:1rem;
}
.prog-bar:before {
  content: "";
  height: 5px;
  width: 85%;
  background: #c8c8c8;
  position: absolute;
  left: 0;
  top: 10px;
}
.prog-bar:after {
  content: "";
  height: 5px;
  width: 12%;
  background: #50cb75;
  position: absolute;
  left: 0;
  top: 10px;
}
.prog-bar.login:after {
  width: 45%;
}
.prog-bar.timeframe:after {
  width: 79%;
}
.prog-bar li {
  width: 33.33333%;
  position: relative;
}
.prog-bar li::before {
  content: "";
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: #c8c8c8;
  display: flex;
  justify-content: center;
  margin: 0 auto;
  margin-bottom: 10px;
}
.prog-bar li.checked::before {
  background: #50cb75;
}
.prog-bar li.checked::after {
  content: "✔";
  color: #fff;
  position: absolute;
  width: 26px;
  height: 26px;
  font-size: 16px;
  top: 0;
  left: 36%;
  z-index: 9;
  display: flex;
  align-items: center;
  justify-content: center;
}
.x-spreadsheet-sheet {
  max-width: 1280px;
}
</style>
